import { useEffect, useMemo, useState } from 'react';
import { Modal, Pressable, View, Switch, ScrollView, Dimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme, type TripFlowTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SubHeading, Body, Caption, ThemedText } from '@/components/ui/Text';
import { SelectField } from '@/components/ui/SelectField';
import { SelectFieldModalProvider } from '@/components/ui/SelectFieldModalContext';
import type { NotificationPreference, NotificationChannelPreferences } from '@/state/userStore';

type NotificationSettingsModalProps = {
  visible: boolean;
  onClose: () => void;
  value: NotificationPreference | undefined;
  onSave: (next: NotificationPreference) => Promise<void> | void;
};

// Extended local state to include all new fields
type LocalPrefsState = {
  // Category toggles
  itinerary: boolean;
  expenses: boolean;
  safety: boolean;
  documents: boolean;
  collaboration: boolean;
  // Channel toggles
  channels: Required<NotificationChannelPreferences>;
  // Quiet hours
  quietHoursEnabled: boolean;
  quietHoursStart: number;
  quietHoursEnd: number;
};

const DEFAULT_PREFS: LocalPrefsState = {
  itinerary: true,
  expenses: true,
  safety: true,
  documents: true,
  collaboration: true,
  channels: {
    push: true,
    email: true,
    inApp: true,
  },
  quietHoursEnabled: false,
  quietHoursStart: 22, // 10 PM
  quietHoursEnd: 7,    // 7 AM
};

const CATEGORY_ROWS: Array<{
  key: 'itinerary' | 'expenses' | 'safety' | 'documents' | 'collaboration';
  title: string;
  description: string;
  icon: keyof typeof Ionicons.glyphMap;
}> = [
  {
    key: 'itinerary',
    title: 'Itinerary',
    description: 'Reminders for upcoming activities, departures, and day plans.',
    icon: 'calendar-outline',
  },
  {
    key: 'expenses',
    title: 'Expenses',
    description: 'Budget alerts, expense updates, and wallet reminders.',
    icon: 'wallet-outline',
  },
  {
    key: 'safety',
    title: 'Safety',
    description: 'Safety tips and location-based nudges during your trip.',
    icon: 'shield-checkmark-outline',
  },
  {
    key: 'documents',
    title: 'Document nudges',
    description: 'Passport/visa reminders and document checklists.',
    icon: 'document-text-outline',
  },
  {
    key: 'collaboration',
    title: 'Collaborator updates',
    description: 'Comments, votes, and collaborator trip activity.',
    icon: 'people-outline',
  },
];

const CHANNEL_ROWS: Array<{
  key: keyof NotificationChannelPreferences;
  title: string;
  description: string;
  icon: keyof typeof Ionicons.glyphMap;
}> = [
  {
    key: 'push',
    title: 'Push Notifications',
    description: 'Receive push notifications on your device.',
    icon: 'notifications-outline',
  },
  {
    key: 'email',
    title: 'Email Notifications',
    description: 'Receive important updates via email.',
    icon: 'mail-outline',
  },
  {
    key: 'inApp',
    title: 'In-App Notifications',
    description: 'Show alerts in the notification bell.',
    icon: 'notifications-circle-outline',
  },
];

// Generate hour options for picker
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`,
}));

const HOUR_SELECT_OPTIONS = HOUR_OPTIONS.map((h) => ({
  label: h.label,
  value: String(h.value),
}));

/** Dark mode uses primary=white; iOS/Android thumbs default light — avoid white-on-white tracks. */
function notificationSwitchProps(theme: TripFlowTheme, on: boolean) {
  const isDark = theme.colorScheme === 'dark';
  const trackActive = isDark ? theme.colors.success : theme.colors.primary;
  const thumbOn = '#ffffff';
  const thumbOff = isDark ? '#c4c4c4' : '#f1f1f1';
  return {
    trackColor: { false: theme.colors.borderMuted, true: trackActive },
    thumbColor: on ? thumbOn : thumbOff,
    ios_backgroundColor: theme.colors.borderMuted,
  };
}

export function NotificationSettingsModal({ visible, onClose, value, onSave }: NotificationSettingsModalProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const [local, setLocal] = useState<LocalPrefsState>(DEFAULT_PREFS);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<'categories' | 'channels' | 'quiet'>('categories');
  
  const sheetHeight = useMemo(() => {
    const screenH = Dimensions.get('window').height || 0;
    const maxH = Math.max(520, screenH - insets.top - theme.spacing(2));
    return Math.min(maxH, 780);
  }, [insets.top, theme]);

  useEffect(() => {
    if (!visible) return;
    setLocal({
      itinerary: value?.itinerary ?? DEFAULT_PREFS.itinerary,
      expenses: value?.expenses ?? DEFAULT_PREFS.expenses,
      safety: value?.safety ?? DEFAULT_PREFS.safety,
      documents: value?.documents ?? DEFAULT_PREFS.documents,
      collaboration: value?.collaboration ?? DEFAULT_PREFS.collaboration,
      channels: {
        push: value?.channels?.push ?? DEFAULT_PREFS.channels.push,
        email: value?.channels?.email ?? DEFAULT_PREFS.channels.email,
        inApp: value?.channels?.inApp ?? DEFAULT_PREFS.channels.inApp,
      },
      quietHoursEnabled: value?.quietHoursStart !== undefined && value?.quietHoursEnd !== undefined,
      quietHoursStart: value?.quietHoursStart ?? DEFAULT_PREFS.quietHoursStart,
      quietHoursEnd: value?.quietHoursEnd ?? DEFAULT_PREFS.quietHoursEnd,
    });
    setActiveSection('categories');
  }, [visible, value]);

  const enabledCategoryCount = useMemo(
    () => [local.itinerary, local.expenses, local.safety, local.documents, local.collaboration].filter(Boolean).length,
    [local],
  );

  const enabledChannelCount = useMemo(
    () => Object.values(local.channels).filter(Boolean).length,
    [local.channels],
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const prefs: NotificationPreference = {
        itinerary: local.itinerary,
        expenses: local.expenses,
        safety: local.safety,
        documents: local.documents,
        collaboration: local.collaboration,
        channels: local.channels,
        quietHoursStart: local.quietHoursEnabled ? local.quietHoursStart : undefined,
        quietHoursEnd: local.quietHoursEnabled ? local.quietHoursEnd : undefined,
      };
      await onSave(prefs);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const toggleAllCategories = (enabled: boolean) => {
    setLocal((prev) => ({
      ...prev,
      itinerary: enabled,
      expenses: enabled,
      safety: enabled,
      documents: enabled,
      collaboration: enabled,
    }));
  };

  const toggleAllChannels = (enabled: boolean) => {
    setLocal((prev) => ({
      ...prev,
      channels: {
        push: enabled,
        email: enabled,
        inApp: enabled,
      },
    }));
  };

  const renderSectionTab = (section: 'categories' | 'channels' | 'quiet', label: string, icon: keyof typeof Ionicons.glyphMap) => (
    <Pressable
      onPress={() => setActiveSection(section)}
      style={{
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: theme.spacing(1),
        paddingHorizontal: theme.spacing(1),
        backgroundColor: activeSection === section ? theme.colors.primary + '20' : 'transparent',
        borderRadius: theme.radii.md,
        gap: theme.spacing(0.5),
      }}
    >
      <Ionicons 
        name={icon} 
        size={16} 
        color={activeSection === section ? theme.colors.primary : theme.colors.textSecondary} 
      />
      <Caption 
        color={activeSection === section ? theme.colors.primary : theme.colors.textSecondary}
        style={{ fontWeight: activeSection === section ? '600' : '400' }}
      >
        {label}
      </Caption>
    </Pressable>
  );

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <SelectFieldModalProvider>
      <View style={{ flex: 1, backgroundColor: theme.colors.modalOverlay, justifyContent: 'flex-end' }}>
        <Pressable style={{ flex: 1 }} onPress={onClose} />

        <View
          style={{
            backgroundColor: theme.colors.surface,
            borderTopLeftRadius: theme.radii.xl,
            borderTopRightRadius: theme.radii.xl,
            height: sheetHeight,
          }}
        >
          {/* Header */}
          <View
            style={{
              padding: theme.spacing(2),
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.borderMuted,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: theme.spacing(1),
            }}
          >
            <View style={{ flex: 1 }}>
              <SubHeading>Notifications</SubHeading>
              <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.25) }}>
                {enabledCategoryCount} categories • {enabledChannelCount} channels
                {local.quietHoursEnabled ? ' • Quiet hours on' : ''}
              </Caption>
            </View>
            <Pressable
              onPress={onClose}
              hitSlop={10}
              accessibilityRole="button"
              accessibilityLabel="Close notifications settings"
            >
              <Ionicons name="close" size={22} color={theme.colors.textSecondary} />
            </Pressable>
          </View>

          {/* Section Tabs */}
          <View
            style={{
              flexDirection: 'row',
              paddingHorizontal: theme.spacing(2),
              paddingVertical: theme.spacing(1),
              gap: theme.spacing(0.5),
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.borderMuted,
            }}
          >
            {renderSectionTab('categories', 'Categories', 'grid-outline')}
            {renderSectionTab('channels', 'Channels', 'radio-outline')}
            {renderSectionTab('quiet', 'Quiet Hours', 'moon-outline')}
          </View>

          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{
              padding: theme.spacing(2),
              paddingBottom: theme.spacing(2),
              gap: theme.spacing(1.25),
            }}
            showsVerticalScrollIndicator={false}
          >
            {/* Categories Section */}
            {activeSection === 'categories' && (
              <>
                <Card variant="outlined">
                  <View style={{ flexDirection: 'row', gap: theme.spacing(1.5), alignItems: 'center' }}>
                    <Ionicons name="options-outline" size={18} color={theme.colors.primary} />
                    <View style={{ flex: 1 }}>
                      <ThemedText variant="bodyStrong">Quick actions</ThemedText>
                      <Caption color={theme.colors.textSecondary}>Enable or disable all categories.</Caption>
                    </View>
                    <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                      <Button
                        label="All on"
                        size="small"
                        variant="secondary"
                        onPress={() => toggleAllCategories(true)}
                      />
                      <Button
                        label="All off"
                        size="small"
                        variant="ghost"
                        onPress={() => toggleAllCategories(false)}
                      />
                    </View>
                  </View>
                </Card>

                {CATEGORY_ROWS.map((row) => (
                  <Card key={row.key} variant="outlined">
                    <View style={{ flexDirection: 'row', gap: theme.spacing(1.5), alignItems: 'center' }}>
                      <Ionicons name={row.icon} size={20} color={theme.colors.textSecondary} />
                      <View style={{ flex: 1 }}>
                        <ThemedText variant="bodyStrong">{row.title}</ThemedText>
                        <Caption color={theme.colors.textSecondary}>{row.description}</Caption>
                      </View>
                      <Switch
                        value={Boolean(local[row.key])}
                        onValueChange={(next) => setLocal((prev) => ({ ...prev, [row.key]: next }))}
                        {...notificationSwitchProps(theme, Boolean(local[row.key]))}
                      />
                    </View>
                  </Card>
                ))}
              </>
            )}

            {/* Channels Section */}
            {activeSection === 'channels' && (
              <>
                <Card variant="outlined">
                  <View style={{ flexDirection: 'row', gap: theme.spacing(1.5), alignItems: 'center' }}>
                    <Ionicons name="options-outline" size={18} color={theme.colors.primary} />
                    <View style={{ flex: 1 }}>
                      <ThemedText variant="bodyStrong">Quick actions</ThemedText>
                      <Caption color={theme.colors.textSecondary}>Enable or disable all channels.</Caption>
                    </View>
                    <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                      <Button
                        label="All on"
                        size="small"
                        variant="secondary"
                        onPress={() => toggleAllChannels(true)}
                      />
                      <Button
                        label="All off"
                        size="small"
                        variant="ghost"
                        onPress={() => toggleAllChannels(false)}
                      />
                    </View>
                  </View>
                </Card>

                {CHANNEL_ROWS.map((row) => (
                  <Card key={row.key} variant="outlined">
                    <View style={{ flexDirection: 'row', gap: theme.spacing(1.5), alignItems: 'center' }}>
                      <Ionicons name={row.icon} size={20} color={theme.colors.textSecondary} />
                      <View style={{ flex: 1 }}>
                        <ThemedText variant="bodyStrong">{row.title}</ThemedText>
                        <Caption color={theme.colors.textSecondary}>{row.description}</Caption>
                      </View>
                      <Switch
                        value={Boolean(local.channels[row.key])}
                        onValueChange={(next) =>
                          setLocal((prev) => ({
                            ...prev,
                            channels: { ...prev.channels, [row.key]: next },
                          }))
                        }
                        {...notificationSwitchProps(theme, Boolean(local.channels[row.key]))}
                      />
                    </View>
                  </Card>
                ))}

                <Card variant="outlined" style={{ backgroundColor: theme.colors.backgroundMuted }}>
                  <View style={{ flexDirection: 'row', gap: theme.spacing(1.5), alignItems: 'flex-start' }}>
                    <Ionicons name="information-circle-outline" size={20} color={theme.colors.textSecondary} />
                    <View style={{ flex: 1 }}>
                      <Caption color={theme.colors.textSecondary}>
                        Disabling a channel stops all notifications through that method. Category settings 
                        still apply - you'll only get notifications for enabled categories through enabled channels.
                      </Caption>
                    </View>
                  </View>
                </Card>
              </>
            )}

            {/* Quiet Hours Section */}
            {activeSection === 'quiet' && (
              <>
                <Card variant="outlined">
                  <View style={{ flexDirection: 'row', gap: theme.spacing(1.5), alignItems: 'center' }}>
                    <Ionicons name="moon-outline" size={20} color={theme.colors.primary} />
                    <View style={{ flex: 1 }}>
                      <ThemedText variant="bodyStrong">Enable Quiet Hours</ThemedText>
                      <Caption color={theme.colors.textSecondary}>
                        Silence non-urgent notifications during set hours.
                      </Caption>
                    </View>
                    <Switch
                      value={local.quietHoursEnabled}
                      onValueChange={(next) => setLocal((prev) => ({ ...prev, quietHoursEnabled: next }))}
                      {...notificationSwitchProps(theme, local.quietHoursEnabled)}
                    />
                  </View>
                </Card>

                {local.quietHoursEnabled && (
                  <>
                    <Card variant="outlined">
                      <View style={{ gap: theme.spacing(1) }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                          <Ionicons name="time-outline" size={18} color={theme.colors.textSecondary} />
                          <ThemedText variant="bodyStrong">Start Time</ThemedText>
                        </View>
                        <SelectField
                          label={undefined}
                          placeholder="Start time"
                          value={String(local.quietHoursStart)}
                          options={HOUR_SELECT_OPTIONS}
                          onValueChange={(value) =>
                            setLocal((prev) => ({ ...prev, quietHoursStart: Number(value) }))
                          }
                        />
                      </View>
                    </Card>

                    <Card variant="outlined">
                      <View style={{ gap: theme.spacing(1) }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                          <Ionicons name="sunny-outline" size={18} color={theme.colors.textSecondary} />
                          <ThemedText variant="bodyStrong">End Time</ThemedText>
                        </View>
                        <SelectField
                          label={undefined}
                          placeholder="End time"
                          value={String(local.quietHoursEnd)}
                          options={HOUR_SELECT_OPTIONS}
                          onValueChange={(value) =>
                            setLocal((prev) => ({ ...prev, quietHoursEnd: Number(value) }))
                          }
                        />
                      </View>
                    </Card>

                    <Card variant="outlined" style={{ backgroundColor: theme.colors.backgroundMuted }}>
                      <View style={{ flexDirection: 'row', gap: theme.spacing(1.5), alignItems: 'flex-start' }}>
                        <Ionicons name="alert-circle-outline" size={20} color={theme.colors.warning} />
                        <View style={{ flex: 1 }}>
                          <Caption color={theme.colors.textSecondary}>
                            <ThemedText variant="caption" style={{ fontWeight: '600' }}>High priority</ThemedText>
                            {' '}notifications (flight delays, safety alerts) will still come through during quiet hours.
                          </Caption>
                        </View>
                      </View>
                    </Card>
                  </>
                )}

                {!local.quietHoursEnabled && (
                  <Card variant="outlined" style={{ backgroundColor: theme.colors.backgroundMuted }}>
                    <View style={{ flexDirection: 'row', gap: theme.spacing(1.5), alignItems: 'flex-start' }}>
                      <Ionicons name="information-circle-outline" size={20} color={theme.colors.textSecondary} />
                      <View style={{ flex: 1 }}>
                        <Caption color={theme.colors.textSecondary}>
                          When enabled, quiet hours will silence medium and low priority notifications between 
                          your chosen start and end times. This is useful for avoiding notifications while you sleep.
                        </Caption>
                      </View>
                    </View>
                  </Card>
                )}
              </>
            )}
          </ScrollView>

          {/* Sticky footer */}
          <View
            style={{
              padding: theme.spacing(2),
              paddingBottom: Math.max(insets.bottom, theme.spacing(2)),
              borderTopWidth: 1,
              borderTopColor: theme.colors.borderMuted,
              gap: theme.spacing(1),
            }}
          >
            <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
              <Button
                label="Cancel"
                variant="ghost"
                onPress={onClose}
                style={{ flex: 1, minHeight: 48 }}
                disabled={saving}
              />
              <Button
                label={saving ? 'Saving...' : 'Save'}
                onPress={handleSave}
                style={{ flex: 1, minHeight: 48 }}
                disabled={saving}
              />
            </View>

            <Body color={theme.colors.textSecondary} style={{ fontSize: 12, lineHeight: 16 }}>
              You can still control system-level notifications from your device settings.
            </Body>
          </View>
        </View>
      </View>
      </SelectFieldModalProvider>
    </Modal>
  );
}
